import { useMutation, UseMutationResult } from "@tanstack/react-query";
import type { UserResponse } from "@xng/xng-backend";
import {
  selectLoggedInClientAssignment,
  selectUser,
} from "../../../../context/slices/userProfileSlice";
import { useXNGDispatch, useXNGSelector } from "../../../../context/store";
import { useNavigate } from "react-router";
import { useMemo, useState } from "react";
import { placeholderForFutureLogErrorText } from "../../../../temp/errorText";
import { API_USERS_BACKEND } from "../../../../api/api";
import { pushError } from "../../../../context/slices/errorEntries";
import { getErrorFromFetchAPIWhenResponseIsOnlyString } from "../../../../utils/get_error_from_fetch_api_when_response_is_only_string";
import { cookieExists, extractCookieValue, setLoggedInUserCookie } from "../../../../utils/cookies";
import { getLoggedInClientAssignment } from "../../../../utils/get_logged_in_client_assignment";
import { ClientAssignment, ClientRef } from "@xng/xng-backend";
import { useClientChangeState } from "../../../../context/clientChangeProvider";

interface ClientSelectorDetails {
  selectableClientOptions: ClientRef[];
  changeClientMutation: UseMutationResult<UserResponse, Error, ClientRef, unknown>;
  selectedClient: ClientRef;
  setSelectedClient: React.Dispatch<React.SetStateAction<ClientRef>>;
}

export function useClientSelectorDetails(): ClientSelectorDetails {
  const userProfile = useXNGSelector(selectUser);
  const dispatch = useXNGDispatch();
  const loggedInClientAssignment = useXNGSelector(selectLoggedInClientAssignment);
  const navigate = useNavigate();
  const { setClientChanging } = useClientChangeState();

  const selectableClientOptions = useMemo(() => {
    const opts =
      userProfile?.clientAssignments
        ?.filter((ca) => (ca as unknown as ClientAssignment).status === 1)
        .map((ca) => ca.client as ClientRef) ?? [];
    return [...opts].sort((a, b) =>
      (a.name ?? "").localeCompare(b.name ?? "", undefined, { sensitivity: "base" }),
    );
  }, [userProfile]);
  const [selectedClient, setSelectedClient] = useState<ClientRef>(loggedInClientAssignment.client!);
  const changeClientMutation = useMutation({
    mutationFn: async (newClient: ClientRef) => {
      if (!userProfile?.id) throw new Error(placeholderForFutureLogErrorText);

      const newClientAssignment = getLoggedInClientAssignment(
        userProfile.clientAssignments!,
        newClient.id!,
      );

      // When logged in as a user from the staff directory, update the cookie that we use to represent a logged in user's client information
      if (cookieExists("originalUserID")) {
        setLoggedInUserCookie(
          userProfile.id,
          extractCookieValue("originalUserID"),
          newClientAssignment.serviceProviderProfile?.id!,
          newClientAssignment.client?.id!,
        );
      }

      return API_USERS_BACKEND.patchUser({
        id: userProfile.id,
        patchUserBody: { loggedInClientId: newClient.id },
      });
    },
    onSuccess: () => navigate(0),
    onError: (error) => {
      displayError();

      async function displayError() {
        dispatch(
          pushError({
            titleID: "Failed to log user into new client!",
            codeBlockMessage: await getErrorFromFetchAPIWhenResponseIsOnlyString(error),
          }),
        );
      }
    },
    onMutate: () => {
      // mark client-change in progress immediately so navigation blockers can bypass
      setClientChanging(true);
      navigate("/xlogs/calendar");
    },
    onSettled: () => {
      // reset flag when mutation finishes (success or error)
      setClientChanging(false);
    },
  });

  return { selectableClientOptions, changeClientMutation, selectedClient, setSelectedClient };
}
